/* Plumme i18n — UI-språk.
 *
 * To akser som IKKE skal blandes:
 *   locale  = brukerens grensesnittspråk (`preferences.locale`, per bruker)
 *   country = foretakets regnskapsland (`settings.country`, per foretak)
 *
 * En bruker med engelsk UI kan føre norsk regnskap, og en norsk bruker kan føre
 * svensk regnskap. Språk styrer tekst, tall- og datoformat. Land styrer valuta,
 * kontoplan, MVA/moms og innleveringer — og skal aldri utledes fra `locale`.
 *
 * Lastes som vanlig <script> (ikke Babel) og må ligge før alle jsx-filer i
 * index.html, siden `data.jsx` og sidene bruker `t()` og formatterne herfra.
 *
 * Bruk:
 *   t('nav.home')                                  -> oversettelse, eller nøkkelen hvis den mangler
 *   t('nav.home', 'Hjem')                          -> med fallback mens en side migreres
 *   t('trial.daysLeft', { days: 5 })               -> med variabler: "Du har {days} dager igjen."
 *   t('trial.daysLeft', 'Du har {days} igjen.', { days: 5 })
 */
(function (global) {
  'use strict';

  // Utvid denne lista når et nytt språk får katalog (se docs/architecture/i18n.md).
  // `required`: katalogen må ha alle nøkler, fordi den er fallback for de andre.
  // Øvrige språk kan mangle nøkler; de vises da på engelsk, så bygget stopper ikke.
  // Speiles i functions/runtime/locale.ts.
  var LOCALES = [
    { code: 'no', label: 'Norsk', short: 'NO', intl: 'nb-NO', required: true },
    { code: 'en', label: 'English', short: 'EN', intl: 'en-GB', required: true },
    { code: 'sv', label: 'Svenska', short: 'SV', intl: 'sv-SE' },
    { code: 'da', label: 'Dansk', short: 'DA', intl: 'da-DK' },
    { code: 'de', label: 'Deutsch', short: 'DE', intl: 'de-DE' },
    { code: 'fr', label: 'Français', short: 'FR', intl: 'fr-FR' },
    { code: 'es', label: 'Español', short: 'ES', intl: 'es-ES' },
    { code: 'pl', label: 'Polski', short: 'PL', intl: 'pl-PL' },
  ];
  var DEFAULT_LOCALE = 'no';
  // Resolve the selected language first, then English, then Norwegian.
  var FALLBACK_LOCALES = ['en', 'no'];
  var STORAGE_KEY = 'plumme-locale';
  var CHANGE_EVENT = 'plumme-locale-changed';

  // Valuta følger foretakets land, ikke brukerens språk.
  var COUNTRY_CURRENCY = { NO: 'NOK', SE: 'SEK', DK: 'DKK' };
  var CURRENCY_SUFFIX = { NOK: 'kr', SEK: 'kr', DKK: 'kr', EUR: '€', USD: '$', GBP: '£' };
  var MINUS = '−';

  var catalogs = {};
  var missing = {};
  var untranslated = {};
  var activeLocale = DEFAULT_LOCALE;

  // Stored domain identifiers may be Norwegian for backwards compatibility,
  // but translation-key references are always English. Keep that conversion
  // explicit instead of leaking persisted values into catalog key names.
  var KEY_REFERENCES = {
    documentType: {
      faktura: 'invoice',
      tilbud: 'quote',
      ordre: 'orderConfirmation',
      kreditnota: 'creditNote',
      annet: 'other',
    },
    route: {
      abonnement: 'subscriptions',
      aiautopilot: 'aiBookkeeper',
      aiinnsikt: 'aiAdvisor',
      aktivitetslogg: 'activityLog',
      allebilag: 'vouchers',
      transaksjon: 'transaction',
      annetsalg: 'otherSales',
      ansattportal: 'employeePortal',
      arsavslutning: 'yearEndClosing',
      arsregnskap: 'annualAccounts',
      budsjett: 'budget',
      dagsoppgjor: 'dailySettlements',
      dokumentasjon: 'documentation',
      eiendeler: 'assets',
      faktura: 'newSale',
      fakturaliste: 'invoices',
      fripostering: 'manualEntry',
      hjem: 'home',
      innboks: 'inbox',
      innsendinger: 'filings',
      innstillinger: 'settings',
      kjorebok: 'mileageLog',
      kunder: 'customers',
      leverandorer: 'suppliers',
      lonn: 'payroll',
      minside: 'myPage',
      nyttkjop: 'newPurchase',
      privatokonomi: 'personalFinance',
      produkter: 'products',
      prosjekter: 'projects',
      resultatregnskap: 'incomeStatement',
      saldobalanse: 'trialBalance',
      skattemelding: 'taxReturn',
      support: 'support',
      varelager: 'inventory',
    },
    status: {
      aktiv: 'active',
      betalt: 'paid',
      forfalt: 'overdue',
      ikke: 'no',
      kreditert: 'credited',
      opprettet: 'created',
      passiv: 'inactive',
      sendt: 'sent',
      snart: 'soon',
      utkast: 'draft',
    },
    crm: {
      aktiv: 'active',
      prospekt: 'prospect',
      tilbudsendt: 'quoteSent',
    },
  };

  var localeEntry = function (code) {
    var wanted = String(code || '').toLowerCase();
    for (var i = 0; i < LOCALES.length; i += 1) {
      if (LOCALES[i].code === wanted) return LOCALES[i];
    }
    return null;
  };
  var isSupported = function (code) { return !!localeEntry(code); };

  var readStoredLocale = function () {
    try {
      var stored = global.localStorage ? global.localStorage.getItem(STORAGE_KEY) : '';
      return isSupported(stored) ? String(stored).toLowerCase() : '';
    } catch (error) { return ''; }
  };

  var writeStoredLocale = function (code) {
    try { if (global.localStorage) global.localStorage.setItem(STORAGE_KEY, code); } catch (error) {}
  };

  var applyDocumentLang = function (code) {
    try {
      if (global.document && global.document.documentElement) {
        global.document.documentElement.lang = localeEntry(code).intl;
      }
    } catch (error) {}
  };

  // ---------------------------------------------------------------- katalog

  // Kataloger kan registreres i flere omganger (én fil per språk, eller senere
  // én fil per domene). Nye nøkler slås sammen inn i det som allerede er lastet.
  var register = function (code, entries) {
    if (!isSupported(code) || !entries) return;
    var target = catalogs[code] || (catalogs[code] = {});
    Object.keys(entries).forEach(function (key) { target[key] = entries[key]; });
  };

  var lookup = function (key) {
    var own = catalogs[activeLocale];
    if (own && own[key] != null) return own[key];
    for (var i = 0; i < FALLBACK_LOCALES.length; i += 1) {
      var fallback = catalogs[FALLBACK_LOCALES[i]];
      if (fallback && fallback[key] != null) return fallback[key];
    }
    return null;
  };

  var interpolate = function (template, vars) {
    if (!vars) return template;
    // Både `{navn}` og `{{navn}}` godtas. Katalogene ble skrevet med dobbel
    // klamme flere steder, og med bare `{navn}`-mønsteret slapp den ytre klammen
    // gjennom til skjermen som «{august 2026}».
    return String(template).replace(/\{\{(\w+)\}\}|\{(\w+)\}/g, function (match, doubled, single) {
      var name = doubled != null ? doubled : single;
      return Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : match;
    });
  };

  // Andre argument kan være enten en fallback-streng eller et variabelobjekt,
  // så migrering av en side ikke trenger et eget kall-mønster.
  var translate = function (key, second, third) {
    var fallback = typeof second === 'string' ? second : null;
    var vars = typeof second === 'object' && second ? second : (third || null);
    if (!catalogs[activeLocale] || catalogs[activeLocale][key] == null) {
      (untranslated[activeLocale] || (untranslated[activeLocale] = {}))[key] = true;
    }
    var value = lookup(key);
    if (value == null) {
      if (!missing[key]) missing[key] = true;
      value = fallback != null ? fallback : key;
    }
    return interpolate(value, vars);
  };

  var has = function (key) { return lookup(key) != null; };
  var missingKeys = function () { return Object.keys(missing).sort(); };
  // Backend responses should carry stable keys and interpolation values.
  // Keep specific legacy messages when the backend has no translated contract.
  var responseMessage = function (payload, fallback) {
    var data = payload || {};
    var key = data.messageKey || (data.domainCode ? 'err.' + data.domainCode : '');
    var message = data.message || data.error || fallback || '';
    return key ? translate(key, String(message), data.messageParams || {}) : String(message);
  };
  // Fallback must not hide missing translations from diagnostics.
  var untranslatedKeys = function (code) {
    var locale = code || activeLocale;
    return Object.keys(untranslated[locale] || {}).filter(function (key) {
      return !catalogs[locale] || catalogs[locale][key] == null;
    }).sort();
  };

  var keyFor = function (namespace, identifier, referenceGroup) {
    var group = KEY_REFERENCES[referenceGroup];
    var value = String(identifier == null ? '' : identifier);
    var reference = group && group[value] ? group[value] : value;
    return String(namespace || '') + '.' + reference;
  };

  // ------------------------------------------------------------ formatering

  var intlLocale = function (code) { return localeEntry(code || activeLocale).intl; };

  var toNumber = function (value) {
    var parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  var number = function (value, options) {
    try { return new Intl.NumberFormat(intlLocale(), options || undefined).format(toNumber(value)); }
    catch (error) { return String(toNumber(value)); }
  };

  var decimalOptions = function (decimals) {
    if (decimals == null) return undefined;
    return { minimumFractionDigits: decimals, maximumFractionDigits: decimals };
  };

  // Bevarer det typografiske minustegnet appen allerede bruker i tallkolonner.
  var signed = function (value, options) {
    var amount = toNumber(value);
    return (amount < 0 ? MINUS : '') + number(Math.abs(amount), options);
  };

  // Foretakets land — settes av backend-state, aldri av språkvalget.
  var businessCountry = function () {
    try {
      var state = global.PlummeBackend && global.PlummeBackend.appState;
      var country = state && state.settings && state.settings.country;
      return country ? String(country).toUpperCase() : 'NO';
    } catch (error) { return 'NO'; }
  };

  var currencyForCountry = function (country) {
    return COUNTRY_CURRENCY[String(country || '').toUpperCase()] || 'NOK';
  };

  var activeCurrency = function () { return currencyForCountry(businessCountry()); };

  var currencySuffix = function (currency) {
    var code = String(currency || activeCurrency()).toUpperCase();
    return CURRENCY_SUFFIX[code] || code;
  };

  // Beløp: tallformat følger språk, valutasuffiks følger foretakets land.
  var money = function (value, options) {
    var config = options || {};
    var decimals = config.decimals == null ? 0 : config.decimals;
    var amount = decimals === 0 ? Math.round(toNumber(value)) : toNumber(value);
    var suffix = config.currency === null ? '' : currencySuffix(config.currency);
    return signed(amount, decimalOptions(decimals)) + (suffix ? ' ' + suffix : '');
  };

  var percent = function (value, options) {
    var amount = toNumber(value);
    var sign = amount > 0 ? '+' : amount < 0 ? MINUS : '';
    return sign + number(Math.abs(amount), options) + ' %';
  };

  var toDate = function (value) {
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (value && typeof value.toDate === 'function') return toDate(value.toDate());
    if (value && typeof value.seconds === 'number') return new Date(value.seconds * 1000);
    if (value == null || value === '') return null;
    var parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  var date = function (value, options) {
    var parsed = toDate(value);
    if (!parsed) return '';
    try { return parsed.toLocaleDateString(intlLocale(), options || undefined); }
    catch (error) { return parsed.toISOString().slice(0, 10); }
  };

  var dateTime = function (value, options) {
    var parsed = toDate(value);
    if (!parsed) return '';
    try { return parsed.toLocaleString(intlLocale(), options || undefined); }
    catch (error) { return parsed.toISOString(); }
  };

  var compare = function (a, b, options) {
    return String(a == null ? '' : a).localeCompare(String(b == null ? '' : b), intlLocale(), options || undefined);
  };

  // -------------------------------------------------------------- livssyklus

  var getLocale = function () { return activeLocale; };

  // `persist` skriver til localStorage slik at neste oppstart maler riktig språk
  // før brukerprofilen er hentet. Backend-eid persistering skjer i preferanser.
  var setLocale = function (code, options) {
    var config = options || {};
    var next = isSupported(code) ? String(code).toLowerCase() : DEFAULT_LOCALE;
    var changed = next !== activeLocale;
    activeLocale = next;
    applyDocumentLang(next);
    if (config.persist !== false) writeStoredLocale(next);
    if (changed || config.force) {
      try { global.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: next })); } catch (error) {}
    }
    return next;
  };

  // Kortkoden som vises i språkvelgeren ('NO' / 'EN').
  var shortCode = function (code) { return localeEntry(code || activeLocale).short; };

  // The loader needs every catalog in the lookup chain, including fallbacks.
  var neededLocales = function () {
    var needed = [activeLocale];
    for (var i = 0; i < FALLBACK_LOCALES.length; i += 1) {
      if (needed.indexOf(FALLBACK_LOCALES[i]) === -1) needed.push(FALLBACK_LOCALES[i]);
    }
    return needed;
  };

  var options = function () {
    return LOCALES.map(function (entry) {
      return { value: entry.code, label: entry.label, short: entry.short };
    });
  };

  activeLocale = readStoredLocale() || DEFAULT_LOCALE;
  applyDocumentLang(activeLocale);

  var api = {
    CHANGE_EVENT: CHANGE_EVENT,
    STORAGE_KEY: STORAGE_KEY,
    locales: function () { return LOCALES.slice(); },
    options: options,
    isSupported: isSupported,
    register: register,
    keyFor: keyFor,
    t: translate,
    has: has,
    missingKeys: missingKeys,
    responseMessage: responseMessage,
    untranslatedKeys: untranslatedKeys,
    getLocale: getLocale,
    setLocale: setLocale,
    shortCode: shortCode,
    neededLocales: neededLocales,
    intlLocale: intlLocale,
    number: number,
    signed: signed,
    money: money,
    percent: percent,
    date: date,
    dateTime: dateTime,
    compare: compare,
    businessCountry: businessCountry,
    currencyForCountry: currencyForCountry,
    activeCurrency: activeCurrency,
    currencySuffix: currencySuffix,
  };

  global.PlummeI18n = api;
  // Global `t` slik at jsx-filene kan bruke den uten oppslag på window.
  global.t = translate;
}(window));
